import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject } from "rxjs";
import { withRestaurantWords } from "./restaurant-words";

/**
 * Turns the restaurant words on for a RESTAURANT company and off for any other one. The labels of each language
 * are kept as loaded, so switching company or language never mixes the two sets of words.
 */
@Injectable({ providedIn: "root" })
export class RestaurantWordsService {
  private active = false;
  private readonly restaurant = new BehaviorSubject(false);
  /** Emits whether the company is a restaurant, now and on every change. */
  readonly restaurant$ = this.restaurant.asObservable();
  /** Labels of each language as loaded from assets/i18n, before any restaurant word. */
  private readonly loaded = new Map<string, Record<string, unknown>>();

  constructor(private readonly translate: TranslateService) {
    // After the switch itself: the translate pipes redraw from the language change event (the file as loaded),
    // so words applied during that event were undone at once ("Products" again until a reload).
    this.translate.onLangChange.subscribe(({ lang }) => queueMicrotask(() => this.apply(lang)));
  }

  /** True once the company is known to be a restaurant. */
  get isRestaurant(): boolean {
    return this.active;
  }

  /** Called with the company's activity once it is known (undefined when it could not be read). */
  setActivity(activity: string | null | undefined): void {
    const active = activity === "RESTAURANT";
    if (active === this.active) {
      return;
    }
    this.active = active;
    this.restaurant.next(active);
    this.apply(this.translate.currentLang);
  }

  private apply(lang: string | undefined): void {
    const current = lang ? this.translate.translations[lang] : undefined;
    if (!lang || !current) {
      // Not loaded yet: onLangChange runs this again once the file is there.
      return;
    }
    if (!this.loaded.has(lang)) {
      this.loaded.set(lang, current);
    }
    const loaded = this.loaded.get(lang) as Record<string, unknown>;
    const next = this.active ? withRestaurantWords(loaded, lang) : loaded;
    if (next !== current) {
      this.translate.setTranslation(lang, next);
    }
  }
}
